import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type { IOvertimePolicy } from "@/types/overtime";

// ─── Mutation param shapes ────────────────────────────────────────────────────

export interface ICreateOvertimePolicyData {
  branchId?: number;
  shiftId?: number;
  dailyThresholdMinutes?: number;
  dailyMaxHours?: number;
  weeklyMaxHours?: number;
  monthlyMaxHours?: number;
  rateMultiplier?: number;
  weekendMultiplier?: number;
  holidayMultiplier?: number;
  requirePreApproval?: boolean;
  autoCapture?: boolean;
  roundingMinutes?: number;
  /**
   * `null` clears the mapping. Distinct from `undefined`, which PATCH leaves
   * untouched — without the null an edit could set an earning but never unset
   * one.
   */
  earningId?: number | null;
}

export interface IUpdateOvertimePolicyData extends Partial<ICreateOvertimePolicyData> {
  id: number | string;
}

// ─── API slice ────────────────────────────────────────────────────────────────

export const companyOvertimePoliciesApi = createApi({
  reducerPath: "companyOvertimePoliciesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getOvertimePolicies"],
  endpoints: (builder) => ({
    getOvertimePolicies: builder.query<IApiResponse<IOvertimePolicy[]>, void>({
      query: () => ({
        url: "/company/overtime-policies",
        method: "GET",
      }),
      providesTags: ["getOvertimePolicies"],
    }),

    getOvertimePolicyById: builder.query<
      IApiResponse<IOvertimePolicy>,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/company/overtime-policies/${id}`,
        method: "GET",
      }),
    }),

    createOvertimePolicy: builder.mutation<
      IApiResponse<IOvertimePolicy>,
      ICreateOvertimePolicyData
    >({
      query: (body) => ({
        url: "/company/overtime-policies",
        method: "POST",
        body,
      }),
      invalidatesTags: ["getOvertimePolicies"],
    }),

    updateOvertimePolicy: builder.mutation<
      IApiResponse<IOvertimePolicy>,
      IUpdateOvertimePolicyData
    >({
      query: ({ id, ...body }) => ({
        url: `/company/overtime-policies/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getOvertimePolicies"],
    }),

    deleteOvertimePolicy: builder.mutation<
      IApiResponse<void>,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/company/overtime-policies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getOvertimePolicies"],
    }),
  }),
});

export const {
  useGetOvertimePoliciesQuery,
  useGetOvertimePolicyByIdQuery,
  useCreateOvertimePolicyMutation,
  useUpdateOvertimePolicyMutation,
  useDeleteOvertimePolicyMutation,
} = companyOvertimePoliciesApi;
